import { platforms, categories, articles } from '@/mocks/data';
import PlatformCard from '@/components/feature/PlatformCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Home() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const featuredArticles = articles.slice(0, 4);
  const hotArticles = articles.slice(4, 8);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢订阅！我们会将最新资讯发送到您的邮箱。');
      setEmail('');
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9n1dg4it35ii3jdlnag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢订阅！我们会将最新资讯发送到您的邮箱。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '榴莲视频',
              url: 'https://durian-video.example.com/',
              description: '榴莲视频对比97视频、丝瓜视频、蓝莓视频等在线视频平台，提供丰富在线影视，糖心视频、黑桃视频等热门平台一网打尽。',
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: '榴莲视频',
              url: 'https://durian-video.example.com/',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://durian-video.example.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: '榴莲视频 - 首页',
              description: '榴莲视频对比97视频、丝瓜视频、蓝莓视频等在线视频平台，提供丰富在线影视。',
              url: 'https://durian-video.example.com/',
              isPartOf: {
                '@type': 'WebSite',
                name: '榴莲视频',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '榴莲视频和97视频哪个更好？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '榴莲视频在画质和综合体验方面更出色，97视频在综艺内容和价格优惠上更有优势。建议根据个人观影偏好选择。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '丝瓜视频的画质怎么样？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '丝瓜视频支持1080P和4K超清画质，采用先进的视频编码技术和CDN加速，画质表现非常出色。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '蓝莓视频的推荐算法好用吗？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '蓝莓视频的智能推荐系统在行业中处于领先地位，融合协同过滤、内容分析和深度学习技术，推荐精准度很高。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '糖心视频和黑桃视频有什么区别？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '糖心视频主打精美设计和优质体验，偏女性用户群体；黑桃视频定位年轻化潮流化，注重差异化内容和创新功能。',
                  },
                },
              ],
            },
          ]),
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20warm%20theater%20atmosphere%20with%20soft%20golden%20and%20coral%20gradients%2C%20elegant%20flowing%20light%20streaks%2C%20modern%20minimal%20style%2C%20dark%20rich%20background%20with%20gentle%20highlights%2C%20no%20people%2C%20artistic%20composition&width=1800&height=900&seq=hero-main-bg&orientation=landscape"
          alt="榴莲视频"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              发现最适合您的
              <span className="text-accent-400">在线视频</span>
              平台
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              榴莲视频为您提供最全面的在线视频平台导航与评测 —— 涵盖97视频、丝瓜视频、蓝莓视频、91在线视频、糖心视频、黑桃视频等热门平台，帮您轻松找到最佳观影选择
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/platform-review"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-line"></i>
                查看平台评测
              </Link>
              <Link
                to="/category/comparison"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 text-white backdrop-blur-sm rounded-lg font-medium text-sm hover:bg-white/25 transition-colors border border-white/20 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-scales-line"></i>
                平台对比分析
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Navigation */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              热门视频平台导航
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              一站式浏览七大主流在线视频平台，快速找到适合您的观影选择
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
            {platforms.map((platform) => (
              <PlatformCard key={platform.id} platform={platform} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              内容分类导航
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              按类别浏览您感兴趣的在线视频平台内容
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center text-center p-6 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-accent-100 rounded-full mb-4 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">{cat.name}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                精选评测文章
              </h2>
              <p className="text-sm text-foreground-500">深度评测各大在线视频平台</p>
            </div>
            <Link
              to="/category/platform-review"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* Hot Articles */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                热门资讯
              </h2>
              <p className="text-sm text-foreground-500">在线视频行业最新动态</p>
            </div>
            <Link
              to="/category/industry-news"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {hotArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm md:text-base text-foreground-500">
              关于在线视频平台的常见疑问解答
            </p>
          </div>
          <div className="space-y-3">
            {[
              {
                q: '榴莲视频和97视频哪个更好？',
                a: '榴莲视频在画质和综合体验方面更出色，支持4K超清和杜比音效；97视频在综艺内容和价格优惠上更有优势。建议根据个人观影偏好选择，两者都提供免费试用。',
              },
              {
                q: '丝瓜视频的画质怎么样？',
                a: '丝瓜视频支持1080P和4K超清画质，采用先进的视频编码技术和CDN加速，画质表现在同类平台中名列前茅，特别适合追求高清观影体验的用户。',
              },
              {
                q: '蓝莓视频的推荐算法好用吗？',
                a: '蓝莓视频的智能推荐系统在行业中处于领先地位，融合协同过滤、内容分析和深度学习技术，推荐精准度很高，能有效帮助用户发现感兴趣的内容。',
              },
              {
                q: '糖心视频和黑桃视频有什么区别？',
                a: '糖心视频主打精美设计和优质体验，偏女性用户群体；黑桃视频定位年轻化潮流化，注重差异化内容和创新功能。两者各有特色，适合不同用户群体。',
              },
              {
                q: '91在线视频的会员值得购买吗？',
                a: '91在线视频性价比很高，年度会员仅需98元，内容丰富且播放稳定。如果你是轻度观影用户，91在线视频是非常实惠的选择。',
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-accent-500">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-mail-send-line text-2xl text-white"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            订阅我们的资讯
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-8">
            第一时间获取在线视频平台最新评测、行业资讯和优惠活动
          </p>
          <form
            onSubmit={handleNewsletterSubmit}
            data-readdy-form
            id="newsletter-form"
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="请输入您的邮箱地址"
              className="flex-1 px-4 py-3 rounded-lg bg-white/15 text-white placeholder-white/50 border border-white/20 focus:outline-none focus:border-white/40 text-sm backdrop-blur-sm"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-white text-accent-700 rounded-lg font-medium text-sm hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即订阅
            </button>
          </form>
          {formStatus !== 'idle' && (
            <p className={`mt-4 text-sm ${formStatus === 'success' ? 'text-white' : 'text-red-200'}`}>
              {formMessage}
            </p>
          )}
        </div>
      </section>
    </>
  );
}